// OP-3605 — Tipos do módulo Estoque e Logística. Espelham o contrato do
// backend (bd-services-producer-analytics, docs/OP-3605-MODULO-ESTOQUE.md).
// Rotas RPC camelCase: id na query no GET, no body no POST.

// Permissões: módulo próprio `ESL` do painel, UMA por função da ferramenta,
// sem perfis cumulativos. A mesma tabela de src/helpers/permissions.ts do backend.
export const ESTOQUE_PERM = {
  VISUALIZAR: 'ESL1',
  SOLICITAR: 'ESL2',
  ATENDER: 'ESL3',
  REMESSAS: 'ESL4',
  TRANSFERIR: 'ESL5',
  APROVAR_TRANSFERENCIA: 'ESL6',
  INVENTARIAR: 'ESL7',
  APROVAR_INVENTARIO: 'ESL8',
  LANCAR_MOVIMENTO: 'ESL9',
  ESTORNAR: 'ESL10',
  VINCULOS: 'ESL11',
  OCORRENCIAS: 'ESL12',
  PROPOR_BAIXA: 'ESL13',
  DECIDIR_BAIXA: 'ESL14',
  MANUTENCAO: 'ESL15',
  PATRIMONIO: 'ESL16',
  CATALOGO: 'ESL17',
  ANEXAR: 'ESL18',
  NOTIFICACOES: 'ESL19',
} as const

/** Capacidade = a chave de ESTOQUE_PERM; o servidor usa os mesmos nomes (domain/acesso.ts). */
export type Capacidade = keyof typeof ESTOQUE_PERM
/** O conjunto de capacidades de quem está logado. */
export type Perfil = ReadonlyArray<Capacidade>

export type Controle = 'QUANTIDADE' | 'SERIAL'
/** Espelho de `domain/estoque.ts`: define o que ainda é coletável. */
export type CicloVida = 'ATIVO' | 'BLOQUEADO' | 'EXTRAVIADO' | 'BAIXADO'
/** Eixo 1 — o que está acontecendo com a peça agora. */
export type Situacao = 'EM_ESTOQUE' | 'SEPARADO' | 'EM_TRANSITO' | 'EM_CAMPO' | 'AGUARDANDO_RETORNO' | 'EM_CONFERENCIA'
/** Eixo 3 — se funciona. */
export type Condicao = 'OK' | 'AVARIADO' | 'EM_MANUTENCAO' | 'SUCATA'
export type TipoLocal = 'PRACA' | 'CLIENTE' | 'PDV' | 'COLABORADOR' | 'FORNECEDOR' | 'TRANSITO' | 'EXTERNO'

export const TIPO_LOCAL_LABEL: Record<TipoLocal, string> = {
  PRACA: 'praça', CLIENTE: 'cliente', PDV: 'PDV', COLABORADOR: 'colaborador',
  FORNECEDOR: 'fornecedor', TRANSITO: 'trânsito', EXTERNO: 'externo',
}

export const rotuloTipoLocal = (t: string | null | undefined): string =>
  t ? TIPO_LOCAL_LABEL[t as TipoLocal] ?? String(t).toLowerCase() : ''

export const lugarDoLocal = (nome: string | null | undefined, tipo: string | null | undefined): string =>
  tipo === 'TRANSITO' ? 'trânsito' : String(nome ?? '')

/** O documento a que um lançamento do razão se refere, resolvido pelo servidor. */
export type DocumentoReferencia = 'PEDIDO' | 'REMESSA' | 'TRANSFERENCIA' | 'CONTRATO' | 'CONTAGEM'

export type TipoMovimento =
  | 'SALDO_INICIAL' | 'ENTRADA' | 'SEPARACAO' | 'CANCELAMENTO_SEPARACAO' | 'SAIDA' | 'ENTREGA'
  | 'COLETA' | 'RECEBIMENTO' | 'CONFERENCIA' | 'SAIDA_TRANSFERENCIA' | 'ENTRADA_TRANSFERENCIA'
  | 'SAIDA_MANUTENCAO' | 'RETORNO_MANUTENCAO' | 'CONSUMO' | 'AJUSTE' | 'EXTRAVIO' | 'BAIXA'

export type StatusSolicitacao =
  | 'EM_ANALISE' | 'AGUARDANDO_INFO' | 'APROVADA' | 'EM_SEPARACAO' | 'EM_ESPERA'
  | 'PRONTA' | 'EXPEDIDA' | 'ATENDIDA_PARCIAL' | 'CONCLUIDA' | 'REJEITADA' | 'CANCELADA'

export type TipoDemanda = 'INSTALACAO' | 'ENVIO' | 'PORTARIA' | 'COLETA'

/** Resposta paginada padrão do módulo (50 por página). */
export interface Paginado<T> {
  rows: T[]
  total: number
  page: number
  lastPage: boolean
}

export interface EstoqueLocal {
  id: number
  tipo: TipoLocal
  nome: string
  pracaId: number | null
  /** Quem está logado pode operar nesta praça (escopo do servidor). */
  noEscopo: boolean
}

// ---- Posição ----
export interface PosicaoRow {
  itemId: number
  codigo: string | null
  nome: string
  controle: Controle
  unidadeMedida: string | null
  fatorCaixa: number | null
  categoria: string
  estoqueMinimo: number | null
  fisico: number
  disponivel: number
  foraDaBase: number
  comprometido: number
  reservado: number
  reservadoPedidos: number
  reservadoTransferencias: number
  extraviadas: number
  critico: boolean
}

/** `getEstoqueOndeTem`: livre por praça; `fisico` só vem para consumível. */
export interface OndeTemRow {
  localId: number
  localNome: string
  fisico?: number
  disponivel: number
}

export interface PosicaoFilters {
  localId?: number | string
  categoriaId?: number | string
  busca?: string
  apenasCriticos?: '1' | ''
  page?: number
  sortBy?: 'item' | 'categoria' | 'fisico' | 'disponivel' | 'foraDaBase' | 'reservado' | 'comprometido'
  sortDir?: 'asc' | 'desc'
}

// ---- Razão ----
export interface MovimentoRow {
  id: number
  tipo: TipoMovimento
  criadoEm: string
  quantidade: number
  usuarioId: number
  usuarioNome: string
  itemId: number
  itemNome: string
  controle: Controle
  unidadeId: number | null
  serial: string | null
  localOrigemId: number | null
  origemNome: string | null
  localDestinoId: number | null
  destinoNome: string | null
  situacaoApos: string | null
  condicaoApos: string | null
  cicloVidaApos: string | null
  referenciaTipo: string
  referenciaId: string | null
  referenciaRefId?: number | null
  /** Documento resolvido pelo servidor; ausente só em backend anterior. */
  referenciaDocumento?: DocumentoReferencia | null
  motivo: string | null
  /** Preenchido quando ESTA linha é o estorno de outra: aponta o original. */
  movimentoEstornoId: number | null
  /** Preenchido quando esta linha JÁ FOI estornada: aponta o inverso. */
  estornadoPorId: number | null
}

export interface MovimentosFilters {
  page?: number
  tipo?: TipoMovimento | ''
  itemId?: number | string
  unidadeId?: number | string
  localId?: number | string
  busca?: string
  de?: string
  ate?: string
}

// ---- Solicitações: a fila (ESL1) e "minhas" (ESL2) são duas leituras ----
// Espelham `SolicitacaoRepository.fila` e `.minhas` do backend.
export interface FilaSolicitacaoRow {
  id: number
  numero: string
  tipoDemanda: TipoDemanda
  status: StatusSolicitacao
  solicitanteNome: string | null
  detentorNome: string | null
  detentorTipo: string | null
  prazoEm: string | null
  previsaoRetorno: string | null
  responsavelNome: string | null
  criadoEm: string
  pracaNome: string
  eventos: number
  eventoNomes: string | null
  /** Somam peça com quantidade: só para compatibilidade, a tela não os exibe. */
  itensPedidos: number
  itensSeparados: number
  /** Já despachado (acumula na expedição; não é "entregue"). */
  itensAtendidos: number
  linhas: number
  linhasCobertas: number
  pecasPedidas: number
  pecasSeparadas: number
  pecasAtendidas: number
  consumoPedido: number
  consumoSeparado: number
  consumoAtendido: number
}

export interface MinhaSolicitacaoRow {
  id: number
  numero: string
  tipoDemanda: TipoDemanda
  status: StatusSolicitacao
  prazoEm: string | null
  criadoEm: string
  detentorNome: string | null
  eventoNomes: string | null
  itens: number
  linhas: number
  linhasCobertas: number
  pecasPedidas: number
  pecasSeparadas: number
  pecasAtendidas: number
  consumoPedido: number
  consumoSeparado: number
  consumoAtendido: number
}

export interface SolicitacoesFilters {
  page?: number
  status?: StatusSolicitacao | ''
  /** '1' esconde os finais quando não há `status` (a Fila); o Histórico não manda. */
  abertos?: '1'
  tipoDemanda?: TipoDemanda | ''
  localId?: number | string
  busca?: string
  sortBy?: string
  sortDir?: 'asc' | 'desc'
}

/** Anexo já gravado, como `getEstoqueAnexos` devolve. */
export interface AnexoSalvo {
  id: number
  escopo: string
  nome: string
  mime: string
  bytes: number
  /** Chave S3: é por ela que a expedição e a entrega apontam o comprovante. */
  chave: string
  /** URL de leitura assinada, TTL curto. */
  url: string
  enviadoPorId: number
  enviadoPorNome: string
  enviadoEm: string
}

/** Anexo já enviado pela URL assinada, pronto para ser salvo junto de uma ação. */
export interface AnexoEnviado {
  key: string
  nome: string
  mime: string
  bytes: number
}

// ---- Apoio ao formulário de solicitação (rotas ESL1 ou ESL2) ----
export interface EstoqueCliente { id: number; nome: string; eventosAptos: number }
export interface EventoApto { id: number; nome: string; inicio: string | null; data: string | null; pracaId: number | null }
export interface TecnicoOption { id: number; nome: string }

/** `getEstoqueItensSolicitacao`: só o que o solicitante precisa para pedir. */
export interface CatalogoItemRow {
  id: number
  codigo: string | null
  nome: string
  controle: Controle
  unidadeMedida: string | null
  tiposDemanda: string | null
  categoria: string
}

/** Campos por tipo de demanda — espelho de `CAMPOS_POR_TIPO` do domínio. */
const ENDERECO = ['cep', 'endereco', 'numero', 'cidade', 'uf'] as const
const CONTATO = ['contatoNome', 'contatoTelefone', 'contatoDocumento'] as const
export const CAMPOS_POR_TIPO: Record<TipoDemanda, string[]> = {
  INSTALACAO: [...ENDERECO, 'dataInstalacao', ...CONTATO],
  ENVIO: [...ENDERECO, 'prazoEntrega', ...CONTATO],
  PORTARIA: ['dataAtendimento'],
  COLETA: [...ENDERECO, 'janelaInicio', ...CONTATO],
}
const ENDERECO_OPCIONAL = ['complemento', 'pontoReferencia']
export const CAMPOS_OPCIONAIS_POR_TIPO: Record<TipoDemanda, string[]> = {
  INSTALACAO: ENDERECO_OPCIONAL,
  ENVIO: ENDERECO_OPCIONAL,
  PORTARIA: [],
  COLETA: ENDERECO_OPCIONAL,
}
export const TAMANHO_MAXIMO_OPCIONAL: Record<string, number> = { complemento: 60, pontoReferencia: 120 }
export const ROTULO_CAMPO: Record<string, string> = {
  cep: 'CEP', endereco: 'Endereço', numero: 'Número', cidade: 'Cidade', uf: 'Estado',
  complemento: 'Complemento', pontoReferencia: 'Ponto de referência',
  prazoEntrega: 'Prazo de entrega', contatoNome: 'Nome do contato no local',
  contatoTelefone: 'Telefone do contato', contatoDocumento: 'CPF ou CNPJ do destinatário', dataInstalacao: 'Data da instalação',
  dataAtendimento: 'Data do atendimento', janelaInicio: 'Coleta a partir de',
}
export const TIPO_DEMANDA_LABEL: Record<TipoDemanda, string> = {
  INSTALACAO: 'Instalação', ENVIO: 'Envio', PORTARIA: 'Portaria', COLETA: 'Coleta',
}
export const UFS_BR = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB',
  'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO']

/** Só identificadores viajam: nome do cliente, do detentor e dos eventos são resolvidos pelo servidor. */
export interface NovaSolicitacaoPayload {
  tipoDemanda: TipoDemanda
  clienteId: number
  tecnicoId: number | null
  localOrigemId: number
  eventos: number[]
  itens: Array<{ itemId: number; quantidade: number }>
  campos: Record<string, string>
  observacao: string | null
  prazoEm?: string | null
}

/** `getEstoqueMinhaSolicitacaoDetalhe`: a leitura reduzida de quem pediu. */
export interface MinhaSolicitacaoDetalhe {
  id: number
  numero: string
  tipoDemanda: TipoDemanda
  status: StatusSolicitacao
  prazoEm: string | null
  detentorNome: string | null
  observacao: string | null
  motivoRecusa: string | null
  criadoEm: string
  itens: Array<{ nome: string; unidadeMedida: string | null; quantidade: number }>
  eventos: Array<{ eventoNome: string; eventoData: string | null }>
  historico: Array<{ para: StatusSolicitacao; mensagem: string | null; criadoEm: string }>
  cancelamento?: CancelamentoDoPedido
  contato?: { nome: string | null; telefone: string | null; documento: string | null }
}

export interface CancelamentoDoPedido {
  pode: boolean
  impedimento: string | null
}

// ---- Detalhe do pedido para a logística (`getEstoqueSolicitacaoDetalhe`) ----
// A consulta devolve `r.*` de req_equip (snake_case) mais quatro listas.
export interface SolicitacaoDetalhe {
  id: number
  numero: string
  cancelamento?: CancelamentoDoPedido
  redirecionamento?: CancelamentoDoPedido
  remessasAbertas?: Array<{ id: number; codigo: string; status: StatusRemessa }>
  separacao?: { fotoObrigatoria: boolean; fotosDaRodada: number; inicioDaRodada?: string | null }
  tipo_demanda: TipoDemanda
  status: StatusSolicitacao
  solicitante_id: number
  solicitante_nome: string
  cliente_id: number
  cliente_nome: string
  detentor_tipo: 'CLIENTE' | 'PDV' | 'COLABORADOR'
  detentor_local_id: number | null
  detentor_referencia: number | null
  detentor_nome: string
  local_origem_id: number
  prazo_em: string | null
  previsao_retorno: string | null
  prazoEm?: string | null
  previsaoRetorno?: string | null
  campos: Record<string, any> | string | null
  observacao: string | null
  motivo_recusa: string | null
  destino_saldo: DestinoSaldo | null
  motivo_saldo: string | null
  criado_em: string
  pracaId: number
  pracaNome: string
  itens: Array<{ id: number; itemId: number; nome: string; controle: Controle; unidadeMedida: string | null; quantidade: number; quantidadeSeparada: number; quantidadeAtendida: number }>
  eventos: Array<{ eventoId: number; eventoNome: string; eventoData: string | null }>
  unidades: Array<{ unidadeId: number; serial: string; patrimonio: string | null; itemNome: string; vinculadoEm: string }>
  historico: Array<{ de: StatusSolicitacao | null; para: StatusSolicitacao; tipo?: 'TRANSICAO' | 'NOTA' | 'REDIRECIONAMENTO'; mensagem: string | null; visivelSolicitante: number; usuarioNome: string; criadoEm: string }>
  transferencias?: Array<{ id: number; numero: string; status: StatusTransferencia; origemNome: string; ladoSolicitante: LadoTransferencia; criadoEm: string }>
}

export type MetodoEnvio = 'EQUIPE_PROPRIA' | 'TRANSPORTADORA'

export const METODO_ENVIO_LABEL: Record<MetodoEnvio, string> = {
  EQUIPE_PROPRIA: 'Equipe própria',
  TRANSPORTADORA: 'Transportadora',
}

export const rotuloMetodoEnvio = (m: string | null | undefined, vazio = '—'): string =>
  m ? METODO_ENVIO_LABEL[m as MetodoEnvio] ?? m : vazio
export type DestinoSaldo = 'SEGUNDA_REMESSA' | 'AGUARDA_TRANSFERENCIA' | 'CANCELAR_SALDO'

/** Espelho de `METODOS_ENVIO` e `DESTINOS_SALDO` do domínio (rótulos da tela). */
export const METODOS_ENVIO: Array<{ metodo: MetodoEnvio; rotulo: string; comprovante: string }> = [
  { metodo: 'EQUIPE_PROPRIA', rotulo: 'Equipe própria leva', comprovante: 'Foto dos equipamentos separados, antes de sair' },
  { metodo: 'TRANSPORTADORA', rotulo: 'Transportadora', comprovante: 'Comprovante de despacho da transportadora' },
]
export const DESTINOS_SALDO: Array<{ destino: DestinoSaldo; rotulo: string; efeito: string }> = [
  { destino: 'SEGUNDA_REMESSA', rotulo: 'Sai numa segunda remessa desta praça', efeito: 'O pedido continua aberto e volta para separação quando houver estoque.' },
  { destino: 'AGUARDA_TRANSFERENCIA', rotulo: 'Aguarda transferência de outra praça', efeito: 'O pedido fica aguardando o equipamento chegar. Abrir a transferência é o passo seguinte.' },
  { destino: 'CANCELAR_SALDO', rotulo: 'O restante não será entregue', efeito: 'Reduz o pedido ao que está saindo. O pedido fecha na entrega; o retorno do equipamento segue na remessa.' },
]

export interface ExpedicaoPayload {
  id: number
  vinculoId: number
  metodoEnvio: MetodoEnvio
  comprovanteDespachoUrl: string
  transportadora: string | null
  eventos: number[]
  destinoSaldo: DestinoSaldo | null
  motivoSaldo: string | null
}

export interface DisponivelRow { id: number; serial: string; patrimonio: string | null }

export interface VinculoResolvido {
  id: number
  codigo: string
  natureza: string
  status: string
  detentorLocalId: number
  vigenciaInicio: string | null
  vigenciaFim: string | null
  vigenciaSugerida: boolean
  uso?: 'CRIAR' | 'REUSAR' | 'RENOVAR'
}

export interface RemessaRow {
  id: number
  codigo: string
  status: string
  metodoEnvio: MetodoEnvio | null
  vinculoId: number | null
  solicitacaoId: number | null
  localOrigemNome: string
  detentorNome: string
  previsaoRetorno: string | null
  prorrogadoPara: string | null
  despachadoEm: string | null
  entregueEm: string | null
  coletadoEm: string | null
  retornoEm: string | null
  encerradoEm: string | null
  transportadora: string | null
  codigoRastreio?: string | null
  comprovanteDespachoUrl?: string | null
  comprovanteEntregaUrl?: string | null
  recebidoPor?: string | null
}

// ---- Remessas (ESL4; leitura ESL1) ----
export type StatusRemessa = 'EM_SEPARACAO' | 'DESPACHADA' | 'ENTREGUE' | 'EM_RETORNO' | 'EM_CONFERENCIA' | 'ENCERRADA' | 'CANCELADA'
export type DecisaoConferencia = 'OK' | 'AVARIADO' | 'SUCATA' | 'NAO_RETORNOU'
export interface RemessaItem {
  id: number
  itemId: number
  itemCodigo: string | null
  itemNome: string
  controle: Controle
  unidadeId: number | null
  serial: string | null
  patrimonio: string | null
  /** Da peça (null em consumível): EXTRAVIADO/BAIXADO não se coleta. */
  cicloVida: CicloVida | null
  quantidade: number
  quantidadeRetornada: number
  emViagem: number
  condicaoRetorno: string | null
  observacaoRetorno: string | null
}

export interface RemessaDetalhe extends RemessaRow {
  pracaId: number
  /** Código do pedido e do contrato que originaram a remessa, para a faixa da vida. */
  solicitacaoNumero: string | null
  vinculoCodigo: string | null
  pecas: number
  pecasRetornadas: number
  pecasPerdidas: number
  observacao: string | null
  criadoEm: string
  canceladoEm?: string | null
  canceladoPorNome?: string | null
  motivoCancelamento?: string | null
  recebidoDocumento?: string | null
  itens: RemessaItem[]
  eventos: Array<{ eventoId: number; origem: string | null }>
}

export interface RemessasFilters {
  status?: StatusRemessa | ''
  /** '1' = só as que ainda respondem por equipamento fora; '0' = só as fechadas. */
  abertas?: '1' | '0'
  page?: number
}

export interface ItemConferidoPayload {
  remessaItemId: number
  decisao: DecisaoConferencia
  quantidadeRetornada?: number
  observacao?: string | null
}

// ---- Vínculos: contrato (detentor externo) ou termo (colaborador); agir ESL11 ----
export type StatusVinculo = 'VIGENTE' | 'ENCERRADO' | 'CANCELADO'
export type NaturezaVinculo = 'CONTRATO' | 'TERMO'

/** Espelha `VinculoRepository.listar` coluna a coluna (as derivadas vêm das remessas do vínculo). */
export interface VinculoRow {
  id: number
  codigo: string
  natureza: NaturezaVinculo
  status: StatusVinculo
  vigenciaInicio: string | null
  vigenciaFim: string | null
  /** 1 enquanto a data é a sugerida pelo evento; 0 depois que a logística a fixou. */
  vigenciaSugerida: number | boolean
  assinadoEm: string | null
  documentoUrl: string | null
  encerradoEm: string | null
  observacao: string | null
  criadoEm: string
  detentorLocalId: number
  detentorTipo: TipoLocal
  detentorNome: string
  pracaId: number | null
  remessasAbertas: number
  /** Peça serializada ainda fora e coletável (não extraviada/baixada). */
  pecasFora: number
  /** Retorno mais próximo entre as remessas abertas (prorrogação sobre previsão). */
  proximoRetorno: string | null
  /** Consumível enviado sob o vínculo — enviado, não consumido. */
  consumoEnviado: number
  requisicoes: number
  /** Peças deste vínculo declaradas extraviadas e ainda no patrimônio. */
  pecasExtraviadas: number
  /** Propostas de baixa abertas sobre peças deste vínculo. */
  baixasPendentes: number
}

export interface VinculoDetalhe extends VinculoRow {
  consumo: Array<{ itemId: number; nome: string; unidadeMedida: string | null; quantidade: number }>
}

export interface VinculosFilters {
  page?: number
  status?: StatusVinculo | ''
  natureza?: NaturezaVinculo | ''
  /**
   * Praça do detentor — `EstoqueLocal.pracaId`, NUNCA `EstoqueLocal.id`.
   * `getEstoqueLocais` devolve os dois e eles são diferentes; mandar o id do
   * local escondia os contratos da praça escolhida.
   */
  pracaId?: number | string
  busca?: string
}

// ---- Equipamentos: a peça, o razão dela e o que a venda testemunha ----

/** `getEstoqueUnidadesDoItem`: peças do item no local (ou cuja base é o local). */
export interface UnidadeRow {
  id: number
  serial: string
  patrimonio: string
  serialFabricante: string | null
  situacao: Situacao
  condicao: Condicao
  cicloVida: CicloVida
  riscoNaoRetorno: number
  /**
   * Onde a peça ESTÁ agora. A consulta traz tanto a peça que está na praça
   * quanto a que só tem base nela (está com cliente, em trânsito, no
   * fornecedor): sem comparar este id, as duas se confundem.
   */
  localId: number
  localNome: string
  localTipo: TipoLocal
  /** A praça dona da peça, mesmo com ela fora. */
  localBaseId: number
  localBaseNome: string
  /** EM_ESTOQUE + OK + ATIVO, calculado pelo servidor. */
  disponivel: number
}

export interface EquipamentoRow extends UnidadeRow {
  itemId: number
  itemNome: string
  itemAtivo: boolean
}

export type PracaDoFiltro = number | 'todas'
export type ItemDoFiltro = number | 'todos'

export interface EquipamentosFilters {
  localId: PracaDoFiltro | null
  itemId: ItemDoFiltro
  busca: string
  page: number
}

/** `getEstoqueUnidadePorCodigo`: a peça pela etiqueta lida, no escopo de quem lê. */
export interface UnidadePorCodigo {
  id: number
  serial: string
  patrimonio: string | null
  serialFabricante: string | null
  itemNome: string
  localNome: string
  casouEm: 'SERIAL' | 'PATRIMONIO' | 'FABRICANTE'
}

export interface UnidadesPorCodigo {
  pecas: UnidadePorCodigo[]
  cortou: boolean
}

/** `getEstoqueUnidade`: o estado da peça, que é o que decide o que a tela oferece. */
export interface UnidadeDetalhe {
  id: number
  itemId: number
  itemNome: string
  itemCodigo: string | null
  controle: Controle
  unidadeMedida: string | null
  serial: string
  patrimonio: string
  serialFabricante: string | null
  terminalId: number | null
  situacao: Situacao
  condicao: Condicao
  cicloVida: CicloVida
  riscoNaoRetorno: number
  observacao: string | null
  fornecedor: string | null
  notaFiscal: string | null
  dataAquisicao: string | null
  valorAquisicao: string | number | null
  proprietario: string | null
  localId: number
  localNome: string
  localTipo: TipoLocal
  localBaseId: number
  localBaseNome: string
  pracaId: number | null
  criadoEm: string
  /** Proposta de baixa ABERTA, se houver: impede propor outra. */
  baixaPropostaId: number | null
}

/** `getEstoqueHistoricoUnidade`: o razão filtrado pela peça. */
export interface HistoricoUnidadeRow {
  id: number
  tipo: TipoMovimento
  criadoEm: string
  usuarioNome: string
  motivo: string | null
  referenciaTipo: string
  referenciaId: string | null
  referenciaRefId?: number | null
  /** Documento resolvido pelo servidor; ausente só em backend anterior. */
  referenciaDocumento?: DocumentoReferencia | null
  situacaoApos: Situacao | null
  condicaoApos: Condicao | null
  cicloVidaApos?: CicloVida | null
  movimentoEstornoId: number | null
  estornadoPorId: number | null
  origemNome: string | null
  destinoNome: string | null
}

/** Força da prova que a venda dá sobre a peça (espelho de `nivelDeCerteza`). */
export type NivelCerteza = 'CONFIRMADO' | 'CORROBORADO' | 'DECLARADO' | 'CONTRADITADO'

/** `getEstoqueUsoUnidade`: leitura, nunca escrita. */
export interface UsoUnidade {
  unidadeId: number
  serial: string
  localRazaoId: number
  localRazaoNome: string
  localRazaoTipo: TipoLocal
  clienteRazaoId: number | null
  terminalId: number | null
  terminalTipo: string | null
  pareado: boolean
  chegouEm: string | null
  entregueEm: string | null
  primeiraVendaPropria: string | null
  ultimaVendaPropria: string | null
  ultimaVendaPropriaNoCiclo: string | null
  primeiraVendaAposEntrega: string | null
  clienteObservadoId: number | null
  localObservadoId: number | null
  ultimaVendaNoLocal: string | null
  nivel: NivelCerteza
  hoje: string
  diasSemVender: number | null
  parada: boolean
  diasAteVender: number | null
}

export interface UnidadesFilters { itemId?: number | string; localId?: number | string }

// ---- Transferências, inventário e ocorrências: os enums do domínio ----
// (as leituras de cada área entram junto das telas que as consomem)

/** Espelho de `StatusTransferencia` (domain/solicitacao.ts). */
export type StatusTransferencia =
  | 'SOLICITADA' | 'APROVADA' | 'EM_SEPARACAO' | 'EM_TRANSITO' | 'RECEBIDA' | 'CONFERIDA' | 'CANCELADA'

/** Espelho de `TipoOcorrencia` e `Natureza` (domain/vinculo.ts). */
export type TipoOcorrencia =
  | 'DETENTOR_DIVERGENTE' | 'VENDA_SEM_REMESSA' | 'VIGENCIA_VENCE_COM_VENDA_ATIVA'
  | 'RETORNO_VENCIDO' | 'PEDIDO_COM_SALDO_PENDENTE'
export type NaturezaOcorrencia = 'ALERTA' | 'DECISAO'

/**
 * A natureza de cada tipo, espelho de `NATUREZA_DE`.
 *
 * ALERTA é o razão e a venda discordando: alguém confere. DECISAO é algo que
 * não está errado e precisa de alguém para decidir o que fazer.
 */
export const NATUREZA_DE: Record<TipoOcorrencia, NaturezaOcorrencia> = {
  DETENTOR_DIVERGENTE: 'ALERTA',
  VENDA_SEM_REMESSA: 'ALERTA',
  VIGENCIA_VENCE_COM_VENDA_ATIVA: 'DECISAO',
  RETORNO_VENCIDO: 'DECISAO',
  PEDIDO_COM_SALDO_PENDENTE: 'DECISAO',
}

// ---- Transferências (criar/operar ESL5; aprovar ESL6; leitura ESL1) ----

/** Cabeçalho, igual em `listar` e `detalhe` (TransferenciaRepository.COLUNAS). */
export interface TransferenciaRow {
  id: number
  numero: string
  status: StatusTransferencia
  requisicaoId: number | null
  requisicaoNumero?: string | null
  ladoSolicitante?: LadoTransferencia
  localOrigemId: number
  origemNome: string
  pracaOrigemId: number | null
  localDestinoId: number
  destinoNome: string
  pracaDestinoId: number | null
  motivo: string | null
  /** Já formatada YYYY-MM-DD pelo servidor. */
  previsaoChegada: string | null
  transportadora: string | null
  codigoRastreio?: string | null
  aprovadoEm: string | null
  despachadoEm: string | null
  recebidoEm: string | null
  conferidoEm: string | null
  criadoEm: string
  criadoPorId: number
  aprovadoPorId: number | null
}

export type LadoTransferencia = 'ORIGEM' | 'DESTINO'

export interface EventoTransferencia {
  statusDe: StatusTransferencia | null
  statusPara: StatusTransferencia
  tipo?: 'TRANSICAO' | 'NOTA'
  motivo: string | null
  usuarioId: number | null
  usuarioNome: string | null
  inferido: boolean
  criadoEm: string
}

export interface ReservaDocumento {
  tipo: 'SOLICITACAO' | 'TRANSFERENCIA'
  numero: string
  status: string
  quantidade: number
}

export interface TransferenciaItem {
  /**
   * Id da LINHA (`est_transferencia_item.id`) — é este que a conferência
   * espera, apesar de o campo do payload se chamar `itemId`.
   */
  id: number
  itemId: number
  itemCodigo: string | null
  itemNome: string
  controle: Controle
  unidadeId: number | null
  serial: string | null
  patrimonio: string | null
  quantidade: number
  quantidadeRecebida: number
  /**
   * OP-3619 — prévia do saldo livre na origem, só em item de quantidade.
   * O servidor manda junto do detalhe para a tela poder avisar ANTES do
   * clique; a recusa de verdade continua sendo a do service, na transação.
   */
  livreNaOrigem?: number
  fisicoNaOrigem?: number
  reservadoNaOrigem?: number
  /** Total pedido DESTE item na transferência, somando linhas repetidas. */
  pedidoDoItem?: number
  /** Quem está segurando o saldo: pedido separado ou transferência aprovada. */
  reservas?: ReservaDocumento[]
  /** Documentos além dos que cabem na tela — o texto diz que há mais. */
  reservasOcultas?: number
  /** Guarda a DECISÃO da conferência, inclusive NAO_RETORNOU. */
  condicaoChegada: DecisaoConferencia | 'NAO_CHEGOU' | null
  observacao: string | null
}

export interface TransferenciaDetalhe extends TransferenciaRow {
  itens: TransferenciaItem[]
  noOrigem?: boolean
  noDestino?: boolean
  criadoPorNome?: string | null
  aprovadoPorNome?: string | null
  historico?: EventoTransferencia[]
}

export interface TransferenciasFilters { status?: StatusTransferencia | ''; page?: number }

/** Uma linha do formulário de criação. */
export interface NovaTransferenciaItem {
  itemId: number
  unidadeId?: number | null
  quantidade?: number
}

// ---- Inventário (contar ESL7; aprovar ESL8; leitura ESL1) ----
export type StatusInventario = 'EM_CONTAGEM' | 'AGUARDANDO_APROVACAO' | 'FECHADO' | 'DESCARTADO'

export interface InventarioRow {
  id: number
  numero: string
  status: StatusInventario
  localId: number
  localNome: string
  pracaId: number | null
  contadoPorId: number
  /** Snapshot gravado na hora: exibir, não resolver pelo id. */
  contadoPorNome: string
  abertoEm: string
  fechadoEm: string | null
  aprovadoPorId: number | null
  aprovadoPorNome: string | null
  aprovadoEm: string | null
  motivoAjuste: string | null
  descartadoEm?: string | null
  descartadoPorId?: number | null
  descartadoPorNome?: string | null
  motivoDescarte?: string | null
}

export interface InventarioUnidade {
  linhaId: number
  unidadeId: number
  serial: string
  patrimonio: string
  encontrada: boolean
  /** null = ninguém tocou. Para o servidor é o mesmo que "não encontrada". */
  marcadaEm: string | null
  marcadaPorId: number | null
}

export interface InventarioLinha {
  /** Id da linha; o POST de contar usa `itemId`, não este. */
  id: number
  itemId: number
  codigo: string | null
  nome: string
  controle: Controle
  esperado: number
  /** null só existe em QUANTIDADE: é "em branco", e em branco não é zero. */
  contado: number | null
  contadoPorId: number | null
  contadoEm: string | null
  /** null quando a linha é de QUANTIDADE. */
  unidades: InventarioUnidade[] | null
}

export interface InventarioDivergencia {
  itemId: number
  nome: string
  esperado: number
  contado: number
  /** contado − esperado; negativo é falta. */
  diferenca: number
}

export interface InventarioDetalhe extends InventarioRow {
  /** Quem abriu, contou uma linha ou marcou uma peça — nenhum deles aprova. */
  participantesIds: number[]
  linhas: InventarioLinha[]
  divergencias: InventarioDivergencia[]
}

export interface InventariosFilters { status?: StatusInventario | ''; page?: number }

// ---- Baixas (propor ESL13; decidir ESL14; anexo ESL18; leitura ESL1) ----
export type StatusBaixa = 'PROPOSTA' | 'APROVADA' | 'RECUSADA'

export interface BaixaRow {
  id: number
  status: StatusBaixa
  motivo: string
  unidadeId: number
  serial: string
  patrimonio: string
  condicao: Condicao
  cicloVida: CicloVida
  itemNome: string
  pracaNome: string
  pracaId: number | null
  propostoPorId: number | null
  propostoPorNome: string
  propostoEm: string
  decididoPorNome: string | null
  decididoEm: string | null
  motivoDecisao: string | null
  movimentoId: number | null
}

export interface BaixasFilters { status?: StatusBaixa | ''; page?: number }

/** `getEstoqueBaixas?semProposta=1`: peça EXTRAVIADO ou SUCATA ainda no patrimônio, sem proposta aberta. */
export interface BaixaSemPropostaRow {
  unidadeId: number
  serial: string
  patrimonio: string
  condicao: Condicao
  cicloVida: CicloVida
  itemNome: string
  pracaNome: string
  pracaId: number | null
  localNome: string
  localTipo?: TipoLocal | null
  desde: string | null
  /** Código do documento do extravio, quando o lançamento o registrou. */
  referenciaId: string | null
}

// ---- Catálogo (criar/trocar controle ESL17; leitura ESL1, sem escopo de praça) ----

/** Veredito do domínio para trocar o regime do item, já calculado pelo servidor. */
export interface Veredito { ok: boolean; erro?: string; http?: number }

export interface ItemCatalogoRow {
  id: number
  codigo: string | null
  nome: string
  controle: Controle
  unidadeMedida: string | null
  fatorCaixa: number | null
  tiposDemanda: string | null
  estoqueMinimo: number | null
  valorReposicao: number | null
  ativo: number
  categoriaId: number
  categoria: string
  unidadesCadastradas: number
  saldoTotal: number
  temMovimento: boolean
  /** Para o regime OPOSTO ao atual: `ok: false` desabilita a troca. */
  trocaControle: Veredito
}

export interface ItemAtributo {
  chave: string
  rotulo: string
  tipo: string
  obrigatorio: number
  ordem: number
}

export interface ItemCatalogoDetalhe extends ItemCatalogoRow {
  atributos: ItemAtributo[]
}

export interface CatalogoFilters {
  busca?: string
  categoriaId?: number | string
  controle?: Controle | ''
  inativos?: 'true' | ''
  page?: number
}

// ---- Ocorrências (resolver ESL12; leitura ESL1) ----
export type Resolucao = 'REGULARIZADO' | 'PRORROGADO' | 'COBRADO' | 'IGNORADO'

/** Gravada em `est_ocorrencia`: tem peça e pode ser resolvida. */
export interface OcorrenciaGravada {
  id: number
  tipo: TipoOcorrencia
  natureza: NaturezaOcorrencia
  detectadoEm: string
  /** Último carimbo da rotina que reconfirmou o fato. */
  vistoEm: string | null
  eventoObservadoId: number | null
  clienteObservadoId: number | null
  /** Limite da pendência nos sinais de prazo. */
  dataReferencia: string | null
  serial: string
  patrimonio: string
  itemNome: string
  remessaCodigo: string | null
  detentorNome: string | null
}

/** Derivada de `req_equip`: `id: null`, não há o que resolver. */
export interface OcorrenciaDerivada {
  id: null
  tipo: 'PEDIDO_COM_SALDO_PENDENTE'
  natureza: 'DECISAO'
  solicitacaoId: number
  numero: string
  detentorNome: string | null
  clienteNome: string | null
  detectadoEm: string
  pedido: number
  atendido: number
  pendente: number
}

export type Ocorrencia = OcorrenciaGravada | OcorrenciaDerivada

// ---- Categorias do catálogo (ler ESL1; criar ESL17) ----
export interface CategoriaRow {
  id: number
  nome: string
  paiId: number | null
  paiNome: string | null
  ordem: number
  ativo: number
  itens: number
}

// ---- Entrada (ESL16): a origem decide o lastro ----
export type OrigemEntrada = 'COMPRA' | 'SALDO_INICIAL' | 'DEVOLUCAO' | 'OUTRA'
export const ORIGENS_ENTRADA: OrigemEntrada[] = ['COMPRA', 'SALDO_INICIAL', 'DEVOLUCAO', 'OUTRA']
export const ORIGEM_ENTRADA_LABEL: Record<OrigemEntrada, string> = {
  COMPRA: 'Compra', SALDO_INICIAL: 'Saldo inicial', DEVOLUCAO: 'Devolução', OUTRA: 'Outra origem',
}

// ---- Hoje (ESL1): o que espera decisão, já no escopo de praça ----
export interface HojeContadores {
  pedidosEmAnalise: number
  pedidosAguardandoInfo: number
  pedidosAprovados: number
  pedidosEmSeparacao: number
  pedidosEmEspera: number
  pedidosProntos: number
  remessasAtrasadas: number
  remessasVencendo: number
  transferenciasAguardandoAprovacao: number
  transferenciasAprovadas: number
  transferenciasEmSeparacao: number
  transferenciasEmTransito: number
  transferenciasAguardandoConferencia: number
  inventariosEmContagem: number
  inventariosAguardandoAprovacao: number
  baixasPendentes: number
  extraviadasSemProposta: number
  ocorrenciasAbertas: number
}

export interface EstoqueHoje {
  hoje: string
  janelaDias: number
  contadores: HojeContadores
}

/** Payload de `createEstoqueItem`. Só `categoriaId`, `codigo`, `nome` e `controle` são exigidos. */
export interface NovoItemPayload {
  categoriaId: number
  codigo: string
  nome: string
  controle: Controle
  unidadeMedida?: string
  fatorCaixa?: number | null
  tiposDemanda?: string
  estoqueMinimo?: number | null
  valorReposicao?: number | null
}

export type PapelNotificacao = 'ATENDIMENTO' | 'REMESSAS'

export interface PessoaNotificacao {
  id: number
  nome: string
  semEmail: boolean
}

export interface InscritoNotificacao extends PessoaNotificacao {
  inativo: boolean
  semAcesso: boolean
  recebe: boolean
}

export interface PapelDaPracaNotificacao {
  papel: PapelNotificacao
  inscritos: InscritoNotificacao[]
  elegiveis: number[]
  usaRegraPadrao: boolean
}

export interface PracaNotificacao {
  pracaId: number
  nome: string
  papeis: PapelDaPracaNotificacao[]
}

export interface PessoaElegivelNotificacao extends PessoaNotificacao {
  todasAsPracas: boolean
}

export interface ConfigNotificacao {
  papeis: PapelNotificacao[]
  maxInscritos: number
  pracas: PracaNotificacao[]
  pessoas: PessoaElegivelNotificacao[]
}
